import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import katex from 'katex';
import 'katex/dist/katex.min.css';

type Align = 'start' | 'center' | 'end';

interface InfoTooltipProps {
  text: string;
  /**
   * Optional LaTeX formula rendered (via KaTeX) as a display-mode block below
   * the text — same visual format as the source template. Math is language-
   * independent, so this is passed from the component, not the i18n locale.
   */
  formula?: string;
  width?: string;
  /**
   * Horizontal anchor of the popover relative to the icon.
   * - `start`: popover's left edge aligns with icon's left edge (opens rightward)
   * - `center`: popover is centered on the icon
   * - `end`: popover's right edge aligns with icon's right edge (opens leftward)
   */
  align?: Align;
}

interface PopoverPos {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  transform?: string;
}

/**
 * The popover is rendered via a portal to <body> with position:fixed so it can
 * never be clipped by an ancestor's overflow (e.g. the evaluation table's
 * `overflow-x-auto` wrapper) or hidden behind a sibling stacking context —
 * which previously cut off the taller metric/formula tooltips.
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, formula, width = 'w-72', align = 'start' }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<PopoverPos | null>(null);

  const show = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const margin = 8;
    // Flip the popover above the icon when there isn't enough room below it
    // (e.g. fields near the bottom of the page, where a tall tooltip would
    // otherwise overflow the viewport bottom and get cut off) and there is
    // more room above. Anchoring by `bottom` lets it grow upward regardless
    // of its height.
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const openUp = spaceBelow < 260 && spaceAbove > spaceBelow;
    const vertical: Pick<PopoverPos, 'top' | 'bottom'> = openUp
      ? { bottom: Math.round(window.innerHeight - r.top + margin) }
      : { top: Math.round(r.bottom + margin) };
    if (align === 'end') {
      setPos({ ...vertical, right: Math.round(window.innerWidth - r.right) });
    } else if (align === 'center') {
      setPos({ ...vertical, left: Math.round(r.left + r.width / 2), transform: 'translateX(-50%)' });
    } else {
      setPos({ ...vertical, left: Math.round(r.left) });
    }
  }, [align]);

  const hide = useCallback(() => setPos(null), []);

  if (!text) return null;

  const formulaHtml = formula
    ? katex.renderToString(formula, { throwOnError: false, displayMode: true })
    : null;

  return (
    <span className="relative inline-flex ml-1">
      <button
        ref={btnRef}
        type="button"
        className="text-muted-foreground hover:text-primary transition-colors"
        onClick={(e) => e.preventDefault()}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-label="Info"
      >
        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
      {pos &&
        createPortal(
          <span
            role="tooltip"
            style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, right: pos.right, transform: pos.transform, zIndex: 9999, maxHeight: 'calc(100vh - 16px)', overflowY: 'auto' }}
            className={`${width} max-w-[92vw] p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg pointer-events-none`}
          >
            <span className="whitespace-pre-line block">{text}</span>
            {formulaHtml && (
              <span
                className="block mt-2 pt-2 border-t border-white/20 overflow-x-auto text-white"
                // KaTeX output is trusted (built from a static in-code LaTeX string,
                // never user input) and rendered with throwOnError:false.
                dangerouslySetInnerHTML={{ __html: formulaHtml }}
              />
            )}
          </span>,
          document.body,
        )}
    </span>
  );
};

export default InfoTooltip;
